import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { BehaviorSubject, firstValueFrom } from 'rxjs';
import { NgxIndexedDBService } from 'ngx-indexed-db';
import * as Sentry from '@sentry/angular';
import {
  ARCHIVED_REQUESTS,
  OPEN_REQUESTS
} from '../../utils/constants/router.constants';
import {
  OrderInEditStatusInterface,
  tableOrdersInEditStatus
} from '../../db-description';
import { BaseHttpService } from '../base-http.service';
import { ConfigService } from '../config.service';
import { Order } from '../../dto/order.dto';
import { BackupService } from '../backup.service';

@Injectable({
  providedIn: 'root'
})
export class OrderChangesService extends BaseHttpService {
  private openOrderSubject: BehaviorSubject<Order | null> =
    new BehaviorSubject<Order | null>(null);

  private workOrderSubject: BehaviorSubject<Order | null> =
    new BehaviorSubject<Order | null>(null);

  private archiveOrderSubject: BehaviorSubject<Order | null> =
    new BehaviorSubject<Order | null>(null);

  // Сериализатор изменений записи заказа в IndexedDB — отдельная цепочка промисов на каждый заказ
  // (B4). Инкремент orderBackupVersion — это read-modify-write; без сериализации два конкурентных
  // вызова checkAndUpdateOrder по одному заказу читают одну и ту же версию и оба пишут v+1: инкремент
  // теряется, а вторая запись в очередь отправки (saveBackup) уходит на уже занятую версию. Для нового
  // заказа несериализованные вызовы дали бы двойной add по одному ключу (ConstraintError) и потерю
  // второй правки. Цепочка на заказ исключает гонку, не блокируя правки разных заказов.
  private orderMutationChains = new Map<number, Promise<void>>();

  constructor(
    protected dbService: NgxIndexedDBService,
    private superHttp: HttpClient,
    private superConfig: ConfigService,
    private backupService: BackupService
  ) {
    super(superHttp, superConfig);

    // Listen for successful server-to-local sync and push fresh order to UI
    try {
      this.backupService.orderSynced$.subscribe(evt => {
        try {
          const orderId = typeof evt === 'number' ? evt : (evt && (evt as any).orderId);
          if (!orderId) {
            return;
          }
          const targetUrl = window.location.pathname.split('/')[1];
          this.dbService.getByKey(tableOrdersInEditStatus, orderId).subscribe({
            next: (rec: any) => {
              if (rec?.Order) {
                this.setOrder(rec.Order as Order, targetUrl);
              }
            },
            error: (error: any) => {
              Sentry.captureException(error, { extra: { operation: 'orderSynced$.getByKey', orderId } });
            }
          });
        } catch (e) {
          Sentry.captureException(e, { extra: { operation: 'orderSynced$.handler' } });
        }
      });
    } catch (e) {
      Sentry.captureException(e, { extra: { operation: 'orderSynced$.subscribe' } });
    }
  }

  /*
   * Возвращаем целевой BehaviorSubject
   * @param targetUrl - используем для определения какой у нас сейчас компонент.
   */
  public getOrderSubject(targetUrl: string): BehaviorSubject<Order | null> {
    switch (targetUrl) {
      case OPEN_REQUESTS:
        return this.openOrderSubject;
      case ARCHIVED_REQUESTS:
        return this.archiveOrderSubject;
      default:
        return this.workOrderSubject;
    }
  }

  public updateRequests(updatedOrder: Order, targetUrl: string) {
    // меняем в списке в базе измененных заказов
    this.checkAndUpdateOrder(updatedOrder);
    this.setOrder(updatedOrder, targetUrl);
  }

  // Метод для передачи нового экземпляра order в сервис
  public setOrder(newOrder: Order, targetUrl: string): void {
    const currentSubj = this.getOrderSubject(targetUrl);
    // new Order review
    currentSubj.next(new Order(newOrder));
  }

  // Функция для проверки наличия записи в IndexedDB и ее обновления или вставки. Постановка идёт в
  // сериализованную по заказу цепочку (B4): read-modify-write версии выполняется атомарно относительно
  // конкурентных вызовов по тому же заказу.
  public checkAndUpdateOrder(updatedOrder: Order): void {
    const orderId = updatedOrder.id;
    const previous = this.orderMutationChains.get(orderId) ?? Promise.resolve();
    // catch на предыдущем звене: сбой одной операции не рвёт цепочку последующих правок того же заказа.
    const next = previous
      .catch(() => undefined)
      .then(() => this.persistOrderIncrement(updatedOrder));
    this.orderMutationChains.set(orderId, next);
    // Убираем цепочку из карты, когда она догналась и новых правок не добавилось (защита от роста карты).
    next.finally(() => {
      if (this.orderMutationChains.get(orderId) === next) {
        this.orderMutationChains.delete(orderId);
      }
    });
  }

  // Одно атомарное звено цепочки: чтение записи → инкремент версии → запись → постановка в очередь
  // отправки. Внутри звена нет наложения с другим звеном того же заказа, поэтому версия читается уже
  // с учётом предыдущего инкремента.
  private async persistOrderIncrement(updatedOrder: Order): Promise<void> {
    try {
      const existingRecord = await firstValueFrom(
        this.dbService.getByKey<OrderInEditStatusInterface>(
          tableOrdersInEditStatus,
          updatedOrder.id
        )
      );

      if (existingRecord) {
        existingRecord.Order = updatedOrder;
        existingRecord.orderBackupVersion =
          +existingRecord.orderBackupVersion + 1;

        if (!existingRecord.user) {
          existingRecord.user = localStorage.getItem('login') ?? undefined;
        }

        await firstValueFrom(
          this.dbService.update(tableOrdersInEditStatus, existingRecord)
        );
        this.backupService.saveBackup(existingRecord.Id);
      } else {
        const newRecord = {
          Id: updatedOrder.id,
          Order: updatedOrder,
          orderBackupVersion: 1
        };

        await firstValueFrom(
          this.dbService.add(tableOrdersInEditStatus, newRecord)
        );
        this.backupService.saveBackup(updatedOrder.id);
      }
    } catch (error) {
      Sentry.captureException(error, {
        extra: { operation: 'checkAndUpdateOrder', orderId: updatedOrder.id }
      });
    }
  }
}
